import type { ReactNode } from 'react'
import { cn } from '@/shared/lib'
import { Icon, type IconName } from './icon'

/**
 * `blue` — успех, использован; `red` — отказ, отмена кассиром;
 * `neutral` — круг `tg-segment-bg`: ошибка сети, отмена, отказ получателя.
 */
export type ResultTone = 'blue' | 'red' | 'neutral'

/** Цвет иконки в нейтральном круге: `hint` — нет сети, `destructive` — платёж не прошёл. */
export type ResultIconTone = 'hint' | 'destructive'

const CIRCLE_CLASS: Record<ResultTone, string> = {
  blue: 'bg-result-blue-bg text-result-blue-ink',
  red: 'bg-result-red-bg text-result-red-ink',
  neutral: 'bg-tg-segment-bg',
}

/** Толщина иконки в круге — по исходнику: check 2.2, x 2, остальные 1.8. */
const STROKE: Record<ResultTone, number> = { blue: 2.2, red: 2, neutral: 1.8 }

type ResultCircleProps = {
  tone: ResultTone
  icon: IconName
  iconTone?: ResultIconTone
  /** `sm` — малый вариант внутри секции (ваучер использован): 56px, иконка 28px. */
  size?: 'md' | 'sm'
  stroke?: number
}

/** Полупрозрачный круг результата 84px с иконкой 38px (`components/ResultState`). */
export const ResultCircle = ({ tone, icon, iconTone = 'hint', size = 'md', stroke }: ResultCircleProps) => (
  <span
    aria-hidden
    className={cn(
      'flex flex-none items-center justify-center rounded-full',
      size === 'md' ? 'size-21' : 'size-14',
      CIRCLE_CLASS[tone],
      tone === 'neutral' && (iconTone === 'destructive' ? 'text-tg-destructive' : 'text-tg-hint'),
    )}
  >
    <Icon name={icon} size={size === 'md' ? 38 : 28} stroke={stroke ?? STROKE[tone]} />
  </span>
)

type ResultStateProps = ResultCircleProps & {
  title: ReactNode
  body?: ReactNode
  /** Под пояснением: технический код, текстовая кнопка. */
  children?: ReactNode
  className?: string
}

/**
 * Полноэкранное состояние-результат: круг → заголовок 20px → пояснение
 * `.lead` по центру экрана, вход `fadeIn .24s`.
 */
export const ResultState = ({ title, body, children, className, ...circle }: ResultStateProps) => (
  <section
    className={cn(
      'flex flex-1 animate-state-in flex-col items-center justify-center gap-2.5 p-9 text-center',
      className,
    )}
  >
    <ResultCircle {...circle} />
    <h1 className="m-0 text-state-title text-balance text-tg-text">{title}</h1>
    {body && <div className="max-w-67.5 text-lead text-pretty text-tg-hint">{body}</div>}
    {children}
  </section>
)
